import { apiRequest } from "./api";
import type { components } from "./api-types";

/**
 * v2.9 P1: the call log — one client for a Curative card's and a lead's (D-120). An entry is recorded
 * after the fact; nothing here places a call or sends anything (CLAUDE.md rule 1).
 */
type S = components["schemas"];
export type CallLogEntry = S["CvCallLogEntry"];
export type CallLogCreate = S["CvCallLogCreate"];
export type CallLogResult = S["CvCallLogResult"];
export type LastCall = S["CvLastCall"];
export type CallOutcome = NonNullable<CallLogEntry["outcome"]>;
export type CallOwner = { kind: "deal" | "lead"; id: string };

const post = <T,>(path: string, body: unknown) =>
  apiRequest<T>(path, { method: "POST", body: JSON.stringify(body) });

export const callLog = {
  log: (owner: CallOwner, body: CallLogCreate) =>
    post<CallLogResult>(
      owner.kind === "deal"
        ? `/api/v1/curative/deals/${owner.id}/call-log`
        : `/api/v1/leads/${owner.id}/call-log`,
      body,
    ),
  update: (
    id: string,
    body: { note?: string | null; outcome?: CallOutcome; callback_at?: string | null; confirm?: boolean },
  ) =>
    apiRequest<CallLogResult>(`/api/v1/curative/call-log/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  remove: (id: string) => apiRequest<void>(`/api/v1/curative/call-log/${id}`, { method: "DELETE" }),
  restore: (id: string) => post<CallLogEntry>(`/api/v1/curative/call-log/${id}/restore`, {}),
  moveToNotes: (id: string) =>
    post<{ note_id: string }>(`/api/v1/curative/call-log/${id}/move-to-notes`, {}),
  convertNote: (owner: CallOwner["kind"], noteId: string, body: { outcome?: CallOutcome } = {}) =>
    post<CallLogEntry>(
      owner === "deal" ? `/api/v1/curative/notes/${noteId}/convert` : `/api/v1/leads/notes/${noteId}/convert`,
      body,
    ),
  pinNote: (owner: CallOwner["kind"], noteId: string, pinned: boolean) =>
    post<unknown>(
      owner === "deal" ? `/api/v1/curative/notes/${noteId}/pin` : `/api/v1/leads/notes/${noteId}/pin`,
      { pinned },
    ),
  savePhones: (owner: CallOwner, phones: string[]) =>
    owner.kind === "deal"
      ? post<unknown>(`/api/v1/curative/deals/${owner.id}/attach-phones`, { phones, source: "note" })
      : post<unknown>(`/api/v1/leads/${owner.id}/phones`, { phones, source: "note" }),
};

/** The quick-log chips, in order (P1.2). */
export const OUTCOME_CHIPS: readonly CallOutcome[] = [
  "connected",
  "no_answer",
  "voicemail_left",
  "callback_requested",
  "wrong_number",
  "not_interested",
  "do_not_call",
];

export const OUTCOME_LABEL: Record<CallOutcome, string> = {
  connected: "Connected",
  no_answer: "No answer",
  voicemail_left: "Voicemail left",
  callback_requested: "Callback requested",
  wrong_number: "Wrong number",
  busy: "Busy",
  not_interested: "Not interested",
  do_not_call: "Do not call",
};

/** The pill's short form: "VM", "NA", "Conn" … (P1.2). */
export const OUTCOME_SHORT: Record<CallOutcome, string> = {
  connected: "Conn",
  no_answer: "NA",
  voicemail_left: "VM",
  callback_requested: "CB",
  wrong_number: "Wrong #",
  busy: "Busy",
  not_interested: "Not int.",
  do_not_call: "DNC",
};

export const OUTCOME_TONE: Record<CallOutcome, "green" | "amber" | "slate" | "rose" | "sky"> = {
  connected: "green",
  no_answer: "slate",
  busy: "slate",
  voicemail_left: "sky",
  callback_requested: "amber",
  wrong_number: "rose",
  not_interested: "rose",
  do_not_call: "rose",
};

export const DEAD_REASON_FROM_OUTCOME: Partial<Record<CallOutcome, string>> = {
  wrong_number: "wrong_number",
  not_interested: "not_interested",
  do_not_call: "unsubscribed",
};

/** "VM 10/19", "NA ×3", "Conn 10/20" — the board card / CALL TODAY / header pill text. */
export function lastCallPill(last: LastCall | null | undefined): string | null {
  if (!last?.outcome) return null;
  const short = OUTCOME_SHORT[last.outcome];
  if ((last.outcome === "no_answer" || last.outcome === "busy") && (last.no_answer_streak ?? 0) >= 2) {
    return `NA ×${last.no_answer_streak}`;
  }
  const d = new Date(last.at);
  const day = d.toLocaleDateString("en-US", { month: "numeric", day: "numeric", timeZone: "America/New_York" });
  return `${short} ${day}`;
}

/** Tomorrow 10:00 in New York, as a value for `<input type="datetime-local">` in the browser's zone. */
export function tomorrowTenLocal(now: Date = new Date()): string {
  const ny = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);
  const [y, m, d] = ny.split("-").map(Number) as [number, number, number];
  const tomorrow = new Date(Date.UTC(y, m - 1, d + 1, 15, 0)); // 10:00 EST/11:00 EDT, refined below
  // Find the UTC instant whose New York wall clock reads 10:00 on that day.
  for (const offset of [14, 15]) {
    const at = new Date(Date.UTC(y, m - 1, d + 1, offset, 0));
    const hour = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York",
      hour: "2-digit",
      hourCycle: "h23",
    }).format(at);
    if (hour === "10") return toLocalInput(at);
  }
  return toLocalInput(tomorrow);
}

export function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** v2.11 (D-145): research notes whose instrument / case numbers parse as phones are never a source. */
export const SYSTEM_NOTE_HEADERS = [
  "TITLE / PROBATE RECORDS",
  "DELINQUENT TAXES",
  "PROBATE / OBIT",
  "HEIRSHIP UPDATE",
  "DISTRESS STACK",
  "CONSOLIDATED",
  "TAX SALE STATUS",
  "BRIEF (PPP-TT)",
] as const;

export function isSystemHeader(body: string): boolean {
  const first = (body.split("\n").find((l) => l.trim()) ?? "").trim().toUpperCase();
  return SYSTEM_NOTE_HEADERS.some((h) => first.startsWith(h));
}

/**
 * US phone numbers typed in a note, E.164 — the browser twin of api/services/call_log_notes.phones_in,
 * D-145's three refusals included: (a) an `ai_analysis` note or a research header; (b) a bare 10-digit run
 * followed by `-<digit>`, inside `(ref …)`, after `#` or `instr`; (c) a bare `(19|20)` + 8-digit run, unless it is
 * written with phone punctuation.
 */
export function phonesIn(body: string, kind?: string | null): string[] {
  if (kind === "ai_analysis" || isSystemHeader(body)) return [];
  const out: string[] = [];
  const re = /(?<![\w$.])\+?1?[\s.()-]*\(?\d{3}\)?[\s.-]*\d{3}[\s.-]*\d{4}(?!\d)/g;
  for (const m of body.matchAll(re)) {
    const raw = m[0].trim();
    const at = m.index ?? 0;
    const end = at + m[0].length;
    const digits = raw.replace(/\D/g, "").slice(-10);
    if (digits.length !== 10 || "01".includes(digits[0]!) || "01".includes(digits[3]!)) continue;
    const dateLike = /^(19|20)\d{8}$/.test(digits);
    if (/^\+?1?\d{10}$/.test(raw)) {
      if (/^-\d/.test(body.slice(end, end + 2))) continue;
      const before = body.slice(Math.max(0, at - 60), at);
      if (/\(\s*ref\b[^)]*$/i.test(before) || /(#|\binstr(?:ument)?\.?(?:\s*(?:no\.?|number|#))?)\s*$/i.test(before)) continue;
      if (dateLike) continue;
    } else if (dateLike && !/\(\d{3}\)\s*\d{3}-\d{4}|\d{3}-\d{3}-\d{4}|\d{3}\.\d{3}\.\d{4}/.test(raw)) {
      continue;
    }
    const e164 = `+1${digits}`;
    if (!out.includes(e164)) out.push(e164);
  }
  return out;
}

const TONE_CLASS: Record<string, string> = {
  green: "bg-green-500/15 text-green-700 dark:text-green-300 border-green-500/30",
  amber: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30",
  slate: "bg-slate-500/15 text-slate-700 dark:text-slate-300 border-slate-500/30",
  rose: "bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30",
  sky: "bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/30",
};

/** The outcome's pill classes — the same colour on a card, a lead, CALL TODAY and the chips. */
export function outcomeClass(outcome: CallOutcome | null | undefined): string {
  return TONE_CLASS[outcome ? OUTCOME_TONE[outcome] : "slate"] ?? "";
}

/** "VM? 0:48", "Conn? 3:12", "No answer 0:00" — an auto outcome's dotted pill (v2.10 P2.4). */
export function autoLabel(outcome: CallOutcome | null | undefined, seconds: number | null | undefined): string {
  const s = Math.max(0, Math.round(seconds ?? 0));
  const clock = `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
  if (outcome === "voicemail_left") return `VM? ${clock}`;
  if (outcome === "connected") return `Conn? ${clock}`;
  if (outcome === "no_answer" || outcome === "busy") return `No answer ${clock}`;
  return `${outcome ? OUTCOME_SHORT[outcome] : "?"} ${clock}`;
}
